import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";
import useAuthToken from "../hooks/useAuthToken";
import { protectedResources } from "../authConfig";
import type { UserInfo, SessionUser, Tenant, UserContextValue } from "../types/UserTypes";
import { createApiClient } from "./apiClient";
import {
  getDevUserOverride,
  setAuthenticatedUserId,
  invalidateIdentityContext,
  IMPERSONATION_ENABLED,
  IMPERSONATION_CHANGED,
  IMPERSONATION_REJECTED,
} from "./devImpersonation";
import Toast from "../components/Toast";
import ImpersonationBanner from "../components/ImpersonationBanner";

export { getDevUserOverride, setDevUserOverride } from "./devImpersonation";

const LEGACY_ACTIVE_TENANT_STORAGE_KEY = "activeTenantId";
const ACTIVE_TENANT_STORAGE_PREFIX = "activeTenantId:";

// ── Dev/E2E overrides (development or explicit staging E2E builds only) ──
const DEV_ROLE_OVERRIDE_KEY = "dev_role_override";
const E2E_OVERRIDES_ENABLED = IMPERSONATION_ENABLED;

export function getDevRoleOverride(): number | null {
  if (!E2E_OVERRIDES_ENABLED) return null;
  const val = localStorage.getItem(DEV_ROLE_OVERRIDE_KEY);
  if (val === "1" || val === "2" || val === "3" || val === "4") return Number(val);
  return null;
}

export function setDevRoleOverride(roleId: number | null): void {
  if (roleId !== null) {
    localStorage.setItem(DEV_ROLE_OVERRIDE_KEY, String(roleId));
  } else {
    localStorage.removeItem(DEV_ROLE_OVERRIDE_KEY);
  }
}

export const getAccountStorageId = (account: AccountInfo | null | undefined): string =>
  account?.homeAccountId || account?.localAccountId || account?.username || "";

export const getActiveTenantStorageKey = (accountStorageId: string): string =>
  accountStorageId
    ? `${ACTIVE_TENANT_STORAGE_PREFIX}${accountStorageId}`
    : LEGACY_ACTIVE_TENANT_STORAGE_KEY;

const getScopedStoredTenantId = (accountStorageId: string): string | null => {
  if (!accountStorageId) return null;
  return localStorage.getItem(getActiveTenantStorageKey(accountStorageId));
};

const getLegacyStoredTenantId = (): string | null =>
  localStorage.getItem(LEGACY_ACTIVE_TENANT_STORAGE_KEY);

const persistScopedTenantId = (accountStorageId: string, tenantId: string | null) => {
  if (!accountStorageId) return;
  const key = getActiveTenantStorageKey(accountStorageId);
  if (tenantId) {
    localStorage.setItem(key, tenantId);
  } else {
    localStorage.removeItem(key);
  }
};

const clearLegacyTenantId = () => {
  localStorage.removeItem(LEGACY_ACTIVE_TENANT_STORAGE_KEY);
};

export const resolveRememberedTenantId = ({
  tenantIds,
  scopedStoredTenantId,
  legacyStoredTenantId,
  apiActiveTenantId,
}: {
  tenantIds: string[];
  scopedStoredTenantId: string | null;
  legacyStoredTenantId: string | null;
  apiActiveTenantId: string | null;
}): string | null => {
  const validScopedTenantId =
    scopedStoredTenantId && tenantIds.includes(scopedStoredTenantId)
      ? scopedStoredTenantId
      : null;
  const validLegacyTenantId =
    legacyStoredTenantId && tenantIds.includes(legacyStoredTenantId)
      ? legacyStoredTenantId
      : null;
  const validApiTenantId =
    apiActiveTenantId && tenantIds.includes(apiActiveTenantId)
      ? apiActiveTenantId
      : null;

  return (
    validScopedTenantId ??
    validLegacyTenantId ??
    validApiTenantId ??
    (tenantIds.length === 1 ? tenantIds[0] : null)
  );
};

const UserContext = createContext<UserContextValue>({
  user: null,
  realUser: null,
  tenants: [],
  activeTenantId: null,
  setActiveTenantId: () => {},
  activeTenant: null,
  isApprover: false,
  loading: true,
  refreshUser: async () => {},
  setUser: () => {},
});

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { token, getToken } = useAuthToken();
  const activeAccount = instance.getActiveAccount() || accounts[0] || null;
  const accountStorageId = getAccountStorageId(activeAccount);

  // Base user from login (no roleId — that comes from the active tenant)
  const [baseUser, setBaseUser] = useState<SessionUser | null>(null);
  const [realUser, setRealUser] = useState<UserInfo | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isApprover, setIsApprover] = useState(false);

  // Active tenant persisted per signed-in MSAL account.
  const [activeTenantId, setTenantIdState] = useState<string | null>(null);
  const activeTenantIdRef = useRef<string | null>(null);
  const setActiveTenantIdState = useCallback((value: string | null) => {
    if (activeTenantIdRef.current !== value) {
      activeTenantIdRef.current = value;
      invalidateIdentityContext();
    }
    setTenantIdState(value);
  }, []);
  const [tenantStorageInitialized, setTenantStorageInitialized] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const realTenants = useRef<Tenant[]>([]);
  const realTenantId = useRef<string | null>(null);
  const [impersonationError, setImpersonationError] = useState(false);

  const setActiveTenantId = useCallback<React.Dispatch<React.SetStateAction<string | null>>>((value) => {
    setTenantStorageInitialized(true);
    setActiveTenantIdState(typeof value === "function" ? value(activeTenantIdRef.current) : value);
  }, [setActiveTenantIdState]);

  // Persist active tenant
  useEffect(() => {
    if (!tenantStorageInitialized || getDevUserOverride()) return;
    realTenantId.current = activeTenantId;
    persistScopedTenantId(accountStorageId, activeTenantId);
  }, [activeTenantId, accountStorageId, tenantStorageInitialized]);

  const normalizeTenantId = (t: Tenant): string => String(t?.tenantId ?? t?.TenantId ?? "");

//   if (accounts.length > 0) {
//     const issuer = accounts[0].idTokenClaims?.iss;
//     console.log("Issuer:", issuer);
// }

  /**
   * ✅ NEW: refreshUser() - re-fetches login payload and updates user/tenants/context.
   * Call this after saving profile changes, switching tenant, etc.
   */
  const refreshUser = useCallback(
  async ({ silent = false } = {}) => {
    // Wait until MSAL interactive flow is done
    if (inProgress !== "none") return;

    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account || !token) return;
    const currentAccountStorageId = getAccountStorageId(account);
    if (currentAccountStorageId !== accountStorageId) return;
    const isCurrentAccount = () => getAccountStorageId(instance.getActiveAccount() || instance.getAllAccounts()[0] || null) === currentAccountStorageId;

    // ✅ Only show global loading when NOT silent
    if (!silent) setLoading(true);

    const currentTenantId = getScopedStoredTenantId(currentAccountStorageId);

    const userIdentifier = {
      identifier: account.homeAccountId,
      businessName: account.name || "",
      firstName: account.name?.split(" ")[0] || "",
      lastName: account.name?.split(" ").slice(1).join(" ") || "",
      //objectId: account.localAccountId,
      tenantId: account.tenantId,
    };

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      if (currentTenantId) headers["X-Tenant-Id"] = String(currentTenantId);

      const api = createApiClient({
        baseUrl: protectedResources.baseURL.endpoint,
        token,
        getToken,
        tenantId: currentTenantId,
        impersonate: false,
      });
      const payload = await api.post<{ user?: SessionUser & Record<string, unknown>; activeTenantId?: string | null; tenants?: Tenant[] }>(
        "userinfo/login", userIdentifier, { tenantRequired: !!currentTenantId, headers },
      );
      if (!isCurrentAccount()) return;

      // Filter to only active tenants (per-tenant membership status)
      const allTenants = payload?.tenants ?? [];
      const tenantList = allTenants.filter((t) => t.isActive !== false);
      setTenants(tenantList);

      const normalizeTenantId = (t: Tenant): string => String(t?.tenantId ?? t?.TenantId ?? "");
      const tenantIds = tenantList.map(normalizeTenantId).filter(Boolean);

      // Honor activeTenantId from the API if present
      const apiActiveTenantId = payload?.activeTenantId ? String(payload.activeTenantId) : null;
      const scopedStoredTenantId = getScopedStoredTenantId(currentAccountStorageId);
      const legacyStoredTenantId = getLegacyStoredTenantId();
      const nextActiveTenantId = resolveRememberedTenantId({
        tenantIds,
        scopedStoredTenantId,
        legacyStoredTenantId,
        apiActiveTenantId,
      });
      realTenants.current = tenantList;
      realTenantId.current = nextActiveTenantId;

      // In dev/E2E override mode, preserve the activeTenantId (cross-tenant impersonation)
      const devOverrideActive = E2E_OVERRIDES_ENABLED && getDevUserOverride();
      if (!devOverrideActive) {
        persistScopedTenantId(currentAccountStorageId, nextActiveTenantId);
        clearLegacyTenantId();
        setTenantStorageInitialized(true);
        setActiveTenantIdState(nextActiveTenantId);
      }

      // Store the session user (minimal — no roleId)
      const apiUser = payload?.user ?? null;
      const sessionUser: SessionUser | null = apiUser
        ? { userId: apiUser.userId, firstName: apiUser.firstName ?? "", lastName: apiUser.lastName ?? "", emailAddress: apiUser.emailAddress ?? "" }
        : null;
      setAuthenticatedUserId(sessionUser?.userId ?? null);

      // Store real user BEFORE dev overrides (with role from first tenant for display)
      if (sessionUser) {
        const firstTenantRole = tenantList[0]?.roleId ?? 0;
        setRealUser({ ...sessionUser, roleId: firstTenantRole, businessName: "" } as UserInfo);
      } else {
        setRealUser(null);
      }

      // Apply dev/E2E user override
      if (E2E_OVERRIDES_ENABLED) {
        const devUserOverride = getDevUserOverride();
        if (devUserOverride) {
          setBaseUser({ userId: devUserOverride.userId, firstName: devUserOverride.firstName ?? "", lastName: devUserOverride.lastName ?? "", emailAddress: devUserOverride.emailAddress ?? "" });

          // If impersonating and the active tenant is not in the login response,
          // inject a synthetic tenant so the impersonated user's tenant works
          const storedTenantId =
            (devUserOverride.tenantId ? String(devUserOverride.tenantId) : null) ||
            getScopedStoredTenantId(currentAccountStorageId) ||
            getLegacyStoredTenantId();
          const tenantFound = storedTenantId && tenantIds.includes(storedTenantId);
          if (storedTenantId && !tenantFound) {
            const devRoleId = devUserOverride.roleId ?? (devUserOverride as any).RoleId ?? 4;
            const storedTenantName = localStorage.getItem("activeTenantName") || storedTenantId;
            const syntheticTenant: Tenant = {
              tenantId: storedTenantId,
              tenantName: storedTenantName,
              roleId: Number(devRoleId),
              isActive: true,
            };
            setTenants([...tenantList, syntheticTenant]);
          }
          if (storedTenantId) {
            setTenantStorageInitialized(true);
            setActiveTenantIdState(storedTenantId);
          }
        } else {
          setBaseUser(sessionUser);
        }
      } else {
        setBaseUser(sessionUser);
      }
    } catch (err) {
      if (!isCurrentAccount() || (err instanceof DOMException && err.name === "AbortError")) return;
      console.error("❌ Error posting user:", err);
      // In dev/E2E override mode, if we have a user override, apply it even when login fails
      if (E2E_OVERRIDES_ENABLED) {
        const devUserOverride = getDevUserOverride();
        if (devUserOverride) {
          setBaseUser({ userId: devUserOverride.userId, firstName: devUserOverride.firstName ?? "", lastName: devUserOverride.lastName ?? "", emailAddress: devUserOverride.emailAddress ?? "" });
          return;
        }
      }
      setBaseUser(null);
      setTenants([]);
    } finally {
      if (!silent && isCurrentAccount()) setLoading(false);
    }
  },
  [instance, accountStorageId, inProgress, token, getToken, setActiveTenantIdState]
);


  // Initial load + whenever tenant changes (selected) / token becomes available
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await refreshUser();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  useEffect(() => {
    setAuthenticatedUserId(null);
    setBaseUser(null);
    setRealUser(null);
    setTenants([]);
    activeTenantIdRef.current = null;
    setTenantIdState(null);
    setTenantStorageInitialized(false);
    setIsApprover(false);
    setLoading(true);
    realTenants.current = [];
    realTenantId.current = null;
    return () => { setAuthenticatedUserId(null); };
  }, [accountStorageId]);

  useEffect(() => {
    const applyImpersonation = () => {
      const target = getDevUserOverride();
      if (target) {
        const tenantId = String(target.tenantId || activeTenantId || "");
        setBaseUser({ ...target, emailAddress: target.emailAddress ?? "" });
        if (tenantId) {
          const tenant = realTenants.current.find(t => normalizeTenantId(t) === tenantId);
          setTenants(tenant ? realTenants.current : [...realTenants.current, {
            tenantId, tenantName: localStorage.getItem("activeTenantName") || tenantId,
            roleId: target.roleId, isActive: true,
          }]);
          setActiveTenantIdState(tenantId);
        }
      } else {
        setDevRoleOverride(null);
        setBaseUser(realUser ? { ...realUser, emailAddress: realUser.emailAddress ?? "" } : null);
        setTenants(realTenants.current);
        setActiveTenantIdState(realTenantId.current);
        clearLegacyTenantId();
        localStorage.removeItem("activeTenantName");
      }
      setIsApprover(false);
    };
    const onRejected = () => setImpersonationError(true);
    window.addEventListener(IMPERSONATION_CHANGED, applyImpersonation);
    window.addEventListener(IMPERSONATION_REJECTED, onRejected);
    return () => {
      window.removeEventListener(IMPERSONATION_CHANGED, applyImpersonation);
      window.removeEventListener(IMPERSONATION_REJECTED, onRejected);
    };
  }, [activeTenantId, realUser, setActiveTenantIdState]);

  // Convenience: active tenant object
  const activeTenant = useMemo(() => {
    const id = String(activeTenantId ?? "");
    if (!id) return null;
    return tenants.find((t) => normalizeTenantId(t) === id) ?? null;
  }, [tenants, activeTenantId]);

  // Derive user with roleId from active tenant — auto-updates on tenant switch
  // Precedence: explicit dev role override > impersonated user role > tenant-derived role
  const user = useMemo<UserInfo | null>(() => {
    if (!baseUser) return null;
    const tenantRoleId = activeTenant?.roleId ?? 0;
    let roleId = tenantRoleId;

    if (E2E_OVERRIDES_ENABLED) {
      const devUserOverride = getDevUserOverride();
      const devRoleOverride = getDevRoleOverride();
      if (devRoleOverride) roleId = devRoleOverride;
      else if (devUserOverride?.roleId) roleId = Number(devUserOverride.roleId);
    }

    return { ...baseUser, roleId, businessName: "" } as UserInfo;
  }, [baseUser, activeTenant]);

  // Expose setUser for DevDrawer compatibility
  const setUser = useCallback((val: React.SetStateAction<UserInfo | null>) => {
    setBaseUser(prev => {
      const currentUser = prev ? { ...prev, roleId: 0, businessName: "" } as UserInfo : null;
      const resolved = typeof val === "function" ? val(currentUser) : val;
      if (resolved) {
        return { userId: resolved.userId, firstName: resolved.firstName ?? "", lastName: resolved.lastName ?? "", emailAddress: resolved.emailAddress ?? "" };
      }
      return null;
    });
  }, []);

  // Check approver status when tenant/user are ready
  useEffect(() => {
    if (!token || !activeTenantId || !baseUser?.userId || !realUser?.userId) {
      setIsApprover(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = createApiClient({
          baseUrl: protectedResources.baseURL.endpoint, token, getToken,
          tenantId: activeTenantId, userId: realUser.userId,
        });
        const data = await api.get<{ isApprover?: boolean }>(
          "userinfo/approver-status",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-Id": String(activeTenantId),
            },
          },
        );
        if (!cancelled) {
          setIsApprover(!!data?.isApprover);
        }
      } catch {
        if (!cancelled) setIsApprover(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, getToken, activeTenantId, baseUser, realUser?.userId]);

  const contextValue = useMemo<UserContextValue>(() => ({
    user,
    realUser,
    setUser,
    tenants,
    activeTenantId,
    setActiveTenantId,
    activeTenant,
    isApprover,
    loading,
    refreshUser,
  }), [user, realUser, setUser, tenants, activeTenantId, setActiveTenantId, activeTenant, isApprover, loading, refreshUser]);

  return (
    <UserContext.Provider value={contextValue}>
      {IMPERSONATION_ENABLED && <ImpersonationBanner />}
      {impersonationError && <Toast
        type="error"
        message="Impersonation was rejected by the API. Restored your signed-in user."
        onClose={() => setImpersonationError(false)}
      />}
      {children}
    </UserContext.Provider>
  );
};

/**
 * Remounts identity-scoped UI (forms, selections, tier/feature providers) whenever
 * the account, tenant or effective identity changes, including impersonation Stop,
 * so no state from the previous user survives into the next session.
 */
export const IdentityScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const accountId = getAccountStorageId(instance.getActiveAccount() || accounts[0] || null);
  const { activeTenantId, user } = useUser();
  const [session, setSession] = useState(0);
  useEffect(() => {
    const bump = () => setSession(s => s + 1);
    window.addEventListener(IMPERSONATION_CHANGED, bump);
    return () => window.removeEventListener(IMPERSONATION_CHANGED, bump);
  }, []);
  return <React.Fragment key={JSON.stringify([accountId, activeTenantId, user?.userId, session])}>{children}</React.Fragment>;
};
